/**
 * Bounds how long a CALLER waits on a promise, without cancelling it.
 *
 * Resolves with `onTimeout()` if `promise` has not settled within `ms`;
 * otherwise passes the settlement (value or rejection) straight through.
 *
 * The underlying promise is deliberately NOT aborted. For a TanStack
 * `ensureQueryData` that means the fetch keeps running and still populates the
 * cache for the next reader, and an in-flight request is not turned into a
 * cached error for every other consumer. This helper bounds one caller's wait
 * and nothing else.
 *
 * Use it wherever a slow dependency must not be able to stall a user-visible
 * step. `axios` on `mwApi` has no default timeout (axios default 0 = never), so
 * "the request will eventually fail" is not a safe assumption.
 *
 * `onTimeout` runs inside the timer callback, where a throw would escape as an
 * uncaught exception and — worse — leave the timeout promise permanently
 * pending, so `Promise.race` would fall back to waiting on the original promise
 * forever: exactly the unbounded wait this helper exists to prevent. It is
 * therefore called inside a `try`, with `onTimeoutError` as the last-resort
 * value. Callers on a user-visible path must supply it.
 */
export const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  onTimeout: () => T,
  onTimeoutError?: T,
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>(resolve => {
    timer = setTimeout(() => {
      let fallback: T;
      try {
        fallback = onTimeout();
      } catch {
        fallback = onTimeoutError as T;
      }
      resolve(fallback);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};
